<script lang="ts" setup>
import type { BlockSerializedChild, BlockSerializedSlotOutlet } from '#layers/block-engine/types/block';
import type { BlockInheritSlots } from '#layers/block-engine/types/block-editor';
import { until, useElementBounding } from '@vueuse/core';
import { computed, onBeforeUnmount, provide, shallowRef, useId, useTemplateRef, watch } from 'vue';
import { useBlockLibraryStore } from '#layers/block-engine/composables/block-editor/library';
import { useBlockEditorContext } from '#layers/block-engine/composables/use-block-editor-context';
import { useBlockConfig } from '#layers/block-engine/utils/block/api/config';
import { blockChildKey } from '#layers/block-engine/utils/block/api/context';
import { useBlockEditorChildBoundsElement } from '#layers/block-engine/utils/block/api/editor-bounds';
import { useBlockSlots, useHorizontalLayout } from '#layers/block-engine/utils/block/api/helpers';
import { blockPreviewIdPrefix, blockRefKey } from '#layers/block-engine/utils/block/const';
import { isBlockSerialized, provideInBlockEditor } from '#layers/block-engine/utils/block/editor';
import { getBlockRootElement } from '#layers/block-engine/utils/block/element';
import { useBlockInteractiveZoneElement } from '#layers/block-engine/utils/block/interactive-zone';
import { useBlockRenderProps } from '#layers/block-engine/utils/block/props';

const {
	child,
	slotName,
	index,
} = defineProps<{
	child: BlockSerializedChild;
	slotName?: string;
	index?: number;
}>();

defineSlots<BlockInheritSlots>();

const { block: editedBlock, variables, insertion, rootContext, bounds, selection, drag } = useBlockEditorContext();
const blockLibraryStore = useBlockLibraryStore();
const horizontal = useHorizontalLayout();

// Retrieve block

const block = computed(() => blockLibraryStore.getBlock(child.blockId));

if (!editedBlock.value) {
	throw new Error('editedBlock is not defined');
}

// Block Props

const {
	getProps,
} = useBlockRenderProps({
	variableIdToName: variables.variableIdToName,
	variables: variables.variableValues,
	getRootContext: rootContext.getRootContextValues,
});

const propValues = computed(() => getProps({
	child,
	childBlock: block.value,
}));

// Slots

const blockSlots = useBlockSlots(block, () => child, propValues);

const childrenBySlot = computed(() => blockSlots.value.reduce((obj, slot) => {
	obj[slot.name] = (child.slots[slot.name] ?? []).map((childId) => editedBlock.value?.children?.[childId]).filter((c) => !!c) as Array<BlockSerializedChild | BlockSerializedSlotOutlet>;
	return obj;
}, {} as Record<string, Array<BlockSerializedChild | BlockSerializedSlotOutlet>>));

// Selection

const selected = computed(() => selection.selectedChildIds.value.includes(child.id));
const hiddenByCanvasDrag = computed(() => {
	return drag.draggedBlock.value?.source === 'canvas' && drag.draggedBlock.value.childId === child.id;
});
const blockConfig = useBlockConfig(block);
const interactiveRoot = useTemplateRef<HTMLElement>('interactiveRoot');
const isBlockInteractive = computed(() => blockConfig.value?.interactive === true);

useBlockInteractiveZoneElement(
	interactiveRoot,
	() => isBlockInteractive.value && !child.id.startsWith(blockPreviewIdPrefix),
);

const draggedCanvasPlaceholderStyle = computed(() => {
	const draggedBounds = hiddenByCanvasDrag.value ? drag.draggedBlock.value?.bounds : undefined;

	if (!draggedBounds) {
		return;
	}

	return {
		width: `${draggedBounds.width}px`,
		height: `${draggedBounds.height}px`,
		flexShrink: 0,
	};
});

function isInsertionTarget(slot: string) {
	return insertion.target.value?.childId === child.id && insertion.target.value?.slotName === slot;
}

/** Returns whether an empty slot should expose the inherited add button. */
function shouldShowEmptyAddButton(slot: string) {
	const slotDefinition = blockSlots.value.find((blockSlot) => blockSlot.name === slot);

	return slot === 'default' || selected.value || isInsertionTarget(slot) || slotDefinition?.showDefaultAddButton === true;
}

// Provide Injections

// We render another block that is not currently edited
if (isBlockSerialized(block.value)) {
	provideInBlockEditor(false);
}

provide(blockChildKey, () => child);

// Bounds

function useBounds() {
	const blockRef = shallowRef<Record<PropertyKey, any> | null>(null);
	const childBoundsElement = useBlockEditorChildBoundsElement(() => child.id);

	function onBlockRef(value: any) {
		blockRef.value = value;
	}

	const blockRootElement = computed(() => {
		if (childBoundsElement.value) {
			return childBoundsElement.value;
		}

		const ref = blockRef.value;

		// Resolve the block's root element across the supported emit shapes:
		// 1. direct component instance with explicit `[blockRefKey]` override (e.g. `Listing.block.vue`)
		// 2. wrapper component with `[blockRefKey]` override (serialized renderer path)
		// 3. wrapper component exposing `.blockRef` (serialized renderer path)
		// 4. raw component instance — `getBlockRootElement` -> `unrefElement` will pull `$el`
		return getBlockRootElement(
			ref?.[blockRefKey]
			?? ref?.blockRef?.[blockRefKey]
			?? ref?.blockRef
			?? ref,
		);
	});

	const computedBounds = useElementBounding(blockRootElement, {
		updateTiming: 'next-frame',
	});

	const randomBoundsId = useId();

	watch([
		computedBounds.x,
		computedBounds.y,
		computedBounds.width,
		computedBounds.height,
		() => slotName,
		() => index,
	], ([x, y, width, height, slotName, index], [oldX, oldY, oldWidth, oldHeight]) => {
		bounds.bounds.value.set(randomBoundsId, {
			x,
			y,
			width,
			height,
			childId: child.id,
			slotName,
			index,
			horizontal: horizontal.value,
		});

		if (oldX !== x || oldY !== y || oldWidth !== width || oldHeight !== height) {
			// Update positions of other blocks
			bounds.updateEvent.trigger(child.id);
		}
	}, { immediate: true });

	bounds.updateEvent.on((id) => {
		if (id !== child.id) {
			computedBounds.update();
		}
	});

	onBeforeUnmount(() => {
		bounds.bounds.value.delete(randomBoundsId);
	});

	return {
		onBlockRef,
	};
}

const { onBlockRef } = useBounds();

// Suspend until the block is loaded

await until(() => block.value);
</script>

<template>
	<div
		v-if="hiddenByCanvasDrag"
		class="pointer-events-none overflow-hidden rounded-md bg-primary/5 opacity-30"
		:style="draggedCanvasPlaceholderStyle"
	/>

	<div
		v-else-if="block"
		ref="interactiveRoot"
		class="contents"
		:class="{ 'pointer-events-none': !isBlockInteractive }"
		@click.stop
	>
		<BlockRender
			:block="child.blockId"
			:props="propValues"
			:context="rootContext.getRootContextValues()"
			:private-context="{
				rootBlockOverride: editedBlock,
			}"
			unwrap-block-definition
			@block:load="insertion.clear()"
			@block:ref="onBlockRef"
		>
			<template
				v-for="(children, slot) of childrenBySlot"
				:key="slot"
				#[slot]
			>
				<!-- Will call `provideInBlockEditor(true)` -->
				<BlockEditorSlotWrapper>
					<BlockEditorRenderChildren
						:children
						:slot-name="slot"
					>
						<template #before-child="{ index: childIndex }">
							<slot
								name="preview-block"
								:child
								:slot-name="slot"
								:index="childIndex"
							/>
						</template>

						<template #add-block="slotProps">
							<slot
								name="add-block"
								v-bind="slotProps"
							/>
						</template>

						<template #preview-block="slotProps">
							<slot
								name="preview-block"
								v-bind="slotProps"
							/>
						</template>

						<template #outlet="slotProps">
							<slot
								name="outlet"
								v-bind="slotProps"
							/>
						</template>
					</BlockEditorRenderChildren>

					<slot
						v-if="children.length === 0 && shouldShowEmptyAddButton(slot)"
						name="add-block"
						:child
						:slot-name="slot"
						:index="children.length"
						:empty="true"
					/>

					<slot
						name="preview-block"
						:child
						:slot-name="slot"
						:index="children.length"
					/>
				</BlockEditorSlotWrapper>
			</template>
		</BlockRender>
	</div>

	<div v-else class="p-1 text-xs text-error">
		Block {{ child.blockId }} not found
	</div>
</template>
